import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresGuest: true, title: '登录' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'dashboard' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: '仪表盘' },
      },
      {
        path: 'posts',
        name: 'posts',
        component: () => import('@/views/posts/PostListView.vue'),
        meta: { title: '文章管理' },
      },
      {
        path: 'posts/new',
        name: 'post-new',
        component: () => import('@/views/posts/PostEditorView.vue'),
        meta: { title: '新建文章' },
      },
      {
        path: 'posts/:id',
        name: 'post-edit',
        component: () => import('@/views/posts/PostEditorView.vue'),
        meta: { title: '编辑文章' },
        props: true,
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/views/categories/CategoryTreeView.vue'),
        meta: { title: '分类管理' },
      },
      {
        path: 'tags',
        name: 'tags',
        component: () => import('@/views/tags/TagListView.vue'),
        meta: { title: '标签管理' },
      },
      {
        path: 'works',
        name: 'works',
        component: () => import('@/views/works/WorkListView.vue'),
        meta: { title: '作品管理' },
      },
      {
        path: 'works/new',
        name: 'work-new',
        component: () => import('@/views/works/WorkEditorView.vue'),
        meta: { title: '新建作品' },
      },
      {
        path: 'works/:id',
        name: 'work-edit',
        component: () => import('@/views/works/WorkEditorView.vue'),
        meta: { title: '编辑作品' },
        props: true,
      },
      {
        path: 'skills',
        name: 'skills',
        component: () => import('@/views/tools/ToolListView.vue'),
        meta: { title: 'Skill 管理' },
        props: { kind: 'SKILL' },
      },
      {
        path: 'skills/new',
        name: 'skill-new',
        component: () => import('@/views/tools/ToolEditorView.vue'),
        meta: { title: '新建 Skill' },
        props: { kind: 'SKILL' },
      },
      {
        path: 'skills/:id',
        name: 'skill-edit',
        component: () => import('@/views/tools/ToolEditorView.vue'),
        meta: { title: '编辑 Skill' },
        props: (route) => ({ kind: 'SKILL', id: route.params.id }),
      },
      {
        path: 'mcps',
        name: 'mcps',
        component: () => import('@/views/tools/ToolListView.vue'),
        meta: { title: 'MCP 管理' },
        props: { kind: 'MCP' },
      },
      {
        path: 'mcps/new',
        name: 'mcp-new',
        component: () => import('@/views/tools/ToolEditorView.vue'),
        meta: { title: '新建 MCP' },
        props: { kind: 'MCP' },
      },
      {
        path: 'mcps/:id',
        name: 'mcp-edit',
        component: () => import('@/views/tools/ToolEditorView.vue'),
        meta: { title: '编辑 MCP' },
        props: (route) => ({ kind: 'MCP', id: route.params.id }),
      },
      {
        path: 'pages',
        name: 'pages',
        component: () => import('@/views/pages/PageListView.vue'),
        meta: { title: '页面管理' },
      },
      {
        path: 'pages/new',
        name: 'page-new',
        component: () => import('@/views/pages/PageEditorView.vue'),
        meta: { title: '新建页面' },
      },
      {
        path: 'pages/:id',
        name: 'page-edit',
        component: () => import('@/views/pages/PageEditorView.vue'),
        meta: { title: '编辑页面' },
        props: true,
      },
      {
        path: 'now',
        name: 'now',
        component: () => import('@/views/now/NowListView.vue'),
        meta: { title: '此刻' },
      },
      {
        path: 'media-library',
        name: 'media-library',
        component: () => import('@/views/assets/AssetLibraryView.vue'),
        meta: { title: '资源库' },
      },
      {
        path: 'nav-menus',
        name: 'nav-menus',
        component: () => import('@/views/nav-menus/NavMenuListView.vue'),
        meta: { title: '导航菜单' },
      },
      {
        path: 'analytics',
        name: 'analytics',
        component: () => import('@/views/analytics/AnalyticsView.vue'),
        meta: { title: '访客统计' },
      },
      {
        path: 'api-tokens',
        name: 'api-tokens',
        component: () => import('@/views/api-tokens/ApiTokenListView.vue'),
        meta: { title: 'API Tokens' },
      },
      {
        path: 'cron',
        name: 'cron',
        component: () => import('@/views/cron/CronStatusView.vue'),
        meta: { title: 'Cron 状态' },
      },
      {
        path: 'search-stats',
        name: 'search-stats',
        component: () => import('@/views/search-stats/SearchStatsView.vue'),
        meta: { title: '搜索统计' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { title: '站点设置' },
      },
    ],
  },
  // OAuth consent — 独立路由 (不挂 MainLayout, 全屏专注). 需 admin 登录.
  // 流程: /api/oauth/authorize → 302 → /oauth/consent?<params> → 用户同意/拒绝.
  {
    path: '/oauth/consent',
    name: 'oauth-consent',
    component: () => import('@/views/oauth/ConsentView.vue'),
    meta: { requiresAuth: true, title: '授权确认' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.authChecked) {
    await auth.bootstrap();
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.requiresGuest && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }
  if (to.meta.title) {
    document.title = `${to.meta.title as string} · s-blog admin`;
  }
  return true;
});

export default router;
